<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import { getCategories, getSuppliers, getTaxes, addSupplierIfNeeded, addProductWithInitialStock, updateProductBasic, saveProductImage, applyStockAdjustment, ensureTax, addCategoryIfNeeded } from '../db/inventory.js';

const props = defineProps({
  open: { type: Boolean, default: false },
  product: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref('');
const categories = ref([]);
const suppliers = ref([]);
const taxes = ref([]);
const allowedTaxes = computed(() => {
  const byRate = new Map();
  for (const t of taxes.value) {
    const rate = Number(t.rate);
    if (rate === 0 || rate === 16) {
      if (!byRate.has(rate)) byRate.set(rate, t);
      else {
        const existing = byRate.get(rate);
        // Mantener el id más bajo de cada tasa
        if (Number(t.id) < Number(existing.id)) byRate.set(rate, t);
      }
    }
  }
  return Array.from(byRate.values()).sort((a, b) => Number(a.rate) - Number(b.rate));
});
const cameraSupported = ref(false);

// Imagen del producto
const imagePreview = ref('');
const imageBase64 = ref('');
const imageMime = ref('');
// Stock original al abrir edición, para calcular delta de ajuste
const originalStock = ref(0);

const isEdit = computed(() => !!(props.product && props.product.id));

const form = ref({
  sku: '',
  barcode: '',
  name: '',
  category_id: null,
  new_category: '',
  supplier_name: '',
  supplier_id: null,
  cost_price: 0,
  unit_price: 0,
  tax_id: null,
  reorder_level: 0,
  initial_qty: 0,
  notes: '',
  discount: 0,
  description: '',
});

onMounted(async () => {
  await loadCatalogs();
  cameraSupported.value = !!(navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
});
onBeforeUnmount(() => {});

watch(() => props.open, (val) => {
  if (val) populateFromProductOrReset();
});
watch(() => props.product, () => {
  if (props.open) populateFromProductOrReset();
});

function resetForm() {
  form.value = {
    sku: '', barcode: '', name: '', category_id: null, new_category: '',
    supplier_name: '', supplier_id: null,
    cost_price: 0, unit_price: 0, tax_id: null,
    reorder_level: 0, initial_qty: 0, notes: '',
    discount: 0, description: '',
  };
  imagePreview.value = '';
  imageBase64.value = '';
  imageMime.value = '';
  originalStock.value = 0;
}

function populateFromProductOrReset() {
  error.value = '';
  if (isEdit.value && props.product) {
    form.value.name = props.product.name || '';
    form.value.sku = props.product.sku || '';
    form.value.barcode = props.product.barcode || '';
    form.value.unit_price = Number(props.product.unit_price || 0);
    form.value.cost_price = Number(props.product.cost_price || 0);
    form.value.reorder_level = Number(props.product.reorder_level || 0);
    form.value.discount = Number(props.product.discount || 0);
    form.value.description = props.product.description || '';
    form.value.notes = props.product.notes || '';
    // Mostrar el stock actual en el campo de inventario cuando se edita
    // (el inventario inicial sólo aplica al crear; en edición se deshabilita)
    form.value.initial_qty = Number(props.product.stock_qty || 0);
    originalStock.value = Number(props.product.stock_qty || 0);
    // Preseleccionar IDs existentes si vienen del listado
    form.value.category_id = props.product.category_id ?? null;
    form.value.supplier_id = props.product.supplier_id ?? null;
    form.value.tax_id = props.product.tax_id ?? null;
    // Imagen existente para previsualización (si está disponible)
    if (props.product.image_base64) {
      imageBase64.value = props.product.image_base64;
      imageMime.value = props.product.image_mime || 'image/jpeg';
      imagePreview.value = `data:${imageMime.value};base64,${imageBase64.value}`;
    } else {
      imagePreview.value = '';
      imageBase64.value = '';
      imageMime.value = '';
    }
    // Otros campos pueden no estar presentes en el listado; se mantienen como están
  } else {
    resetForm();
  }
  // nextTick(() => renderBarcode(form.value.barcode || ''));
}

async function loadCatalogs() {
  loading.value = true;
  try {
    categories.value = await getCategories();
    suppliers.value = await getSuppliers();
    // Asegurar que existan IVA 0% e IVA 16%
    await ensureTax(0, 'IVA');
    await ensureTax(16, 'IVA 16%');
    // Recargar y limitar a 0% y 16%
    taxes.value = (await getTaxes()).filter(t => [0,16].includes(Number(t.rate)));
  } catch (e) {
    error.value = String(e);
  } finally {
    loading.value = false;
  }
}

function onNewCategoryInput() {
  // When user types in new category, clear the selected category
  if (form.value.new_category?.trim()) {
    form.value.category_id = null;
  }
}

function onCategoryChange() {
  // When user selects a category, clear the new category input
  if (form.value.category_id) {
    form.value.new_category = '';
  }
}

function close() {
  emit('close');
}

async function save() {
  error.value = '';
  loading.value = true;
  try {
    if (!form.value.name?.trim()) throw new Error('Nombre es requerido');
    if (!form.value.unit_price && !form.value.cost_price) throw new Error('Precio o costo requerido');
    // El precio guardado (unit_price) es siempre base SIN IVA.
    // Solo se guarda el tax_id elegido; el IVA se aplica al mostrar.
    // No modificar form.value.unit_price aquí para evitar duplicación.

    if (isEdit.value && props.product?.id) {
      // Manejar nueva categoría si se especifica
      let categoryId = form.value.category_id;
      console.log('🔍 Editando producto:', {
        category_id_seleccionado: form.value.category_id,
        new_category: form.value.new_category,
        categoryId_final: categoryId
      });
      
      if (!categoryId && form.value.new_category?.trim()) {
        console.log('� SE VA A CREAR NUEVA CATEGORÍA (EDICIÓN):', form.value.new_category.trim());
        categoryId = await addCategoryIfNeeded(form.value.new_category.trim());
        console.log('🔥 CATEGORÍA CREADA CON ID (EDICIÓN):', categoryId);
      } else {
        console.log('ℹ️ No se necesita crear nueva categoría (EDICIÓN). categoryId:', categoryId, 'new_category:', form.value.new_category);
      }
      
      // Manejar nuevo proveedor si se especifica
      let supplierId = form.value.supplier_id;
      if (!supplierId && form.value.supplier_name?.trim()) {
        supplierId = await addSupplierIfNeeded(form.value.supplier_name.trim());
      }
      
      await updateProductBasic({
        id: props.product.id,
        sku: (form.value.sku && form.value.sku.trim()) ? form.value.sku.trim() : null,
        barcode: (form.value.barcode && form.value.barcode.trim()) ? form.value.barcode.trim() : null,
        name: form.value.name.trim(),
        unit_price: Number(form.value.unit_price || 0),
        cost_price: Number(form.value.cost_price || 0),
        reorder_level: Number(form.value.reorder_level || 0),
        discount: Number(form.value.discount || 0),
        description: form.value.description || null,
        notes: form.value.notes || null,
        category_id: categoryId || null,
        supplier_id: supplierId || null,
        tax_id: form.value.tax_id,
      });
      // Aplicar ajuste automático si el valor de stock fue modificado
      const desired = Number(form.value.initial_qty || 0);
      const current = Number(originalStock.value || 0);
      const delta = desired - current;
      if (delta !== 0) {
        await applyStockAdjustment({
          product_id: props.product.id,
          quantity: delta,
          unit_value: delta > 0 ? Number(form.value.cost_price || 0) : Number(form.value.unit_price || 0),
          notes: form.value.notes || 'Ajuste por edición de stock',
        });
      }
      // Si se seleccionó una nueva imagen durante edición, guardarla
      if (imageBase64.value) {
        await saveProductImage(props.product.id, imageBase64.value, imageMime.value || 'image/jpeg');
      }
      emit('saved', props.product.id);
    } else {
      let supplierId = form.value.supplier_id;
      if (!supplierId && form.value.supplier_name?.trim()) {
        supplierId = await addSupplierIfNeeded(form.value.supplier_name.trim());
      }
      
      let categoryId = form.value.category_id;
      if (!categoryId && form.value.new_category?.trim()) {
        console.log('🔥 SE VA A CREAR NUEVA CATEGORÍA:', form.value.new_category.trim());
        categoryId = await addCategoryIfNeeded(form.value.new_category.trim());
        console.log('🔥 CATEGORÍA CREADA CON ID:', categoryId);
      } else {
        console.log('ℹ️ No se necesita crear nueva categoría. categoryId:', categoryId, 'new_category:', form.value.new_category);
      }
      
      const productId = await addProductWithInitialStock({
        sku: form.value.sku || null,
        barcode: form.value.barcode || null,
        name: form.value.name.trim(),
        category_id: categoryId || null,
        supplier_id: supplierId || null,
        cost_price: Number(form.value.cost_price || 0),
        unit_price: Number(form.value.unit_price || 0),
        tax_id: form.value.tax_id || null,
        reorder_level: Number(form.value.reorder_level || 0),
        initial_qty: Number(form.value.initial_qty || 0),
        notes: form.value.notes || null,
        image_base64: imageBase64.value || null,
        image_mime: imageMime.value || 'image/jpeg',
        discount: Number(form.value.discount || 0),
        description: form.value.description || null,
      });
      emit('saved', productId);
    }
    resetForm();
    close();
  } catch (e) {
    error.value = String(e);
  } finally {
    loading.value = false;
  }
}



async function onFileChange(ev) {
  const file = ev.target.files?.[0];
  if (!file) return;
  imageMime.value = file.type || 'image/jpeg';
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = String(reader.result || '');
    imagePreview.value = dataUrl;
    const commaIdx = dataUrl.indexOf(',');
    imageBase64.value = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : '';
  };
  reader.onerror = () => { error.value = 'No se pudo leer la imagen'; };
  reader.readAsDataURL(file);
}

function clearImage() {
  imagePreview.value = '';
  imageBase64.value = '';
  imageMime.value = '';
}
</script>

<template>
  <div v-if="open" class="modal-backdrop" @click.self="close">
    <div class="modal">
      <header class="modal-header">
        <h2>{{ isEdit ? 'Editar producto' : 'Nuevo producto e inventario inicial' }}</h2>
        <button class="close" type="button" @click="close" aria-label="Cerrar">×</button>
      </header>

  <section class="modal-body">
        <p v-if="error" class="error">{{ error }}</p>

        <div class="grid">
          <label class="span-2">
            Imagen del producto
            <div class="image-row">
              <input type="file" accept="image/*" @change="onFileChange" />
              <button class="btn secondary" type="button" @click="clearImage" :disabled="!imagePreview">Quitar</button>
            </div>
            <div v-if="imagePreview" class="image-preview">
              <img :src="imagePreview" alt="Previsualización" />
            </div>
          </label>
          <div class="row-2 span-2">
            <label>
              SKU
              <input v-model="form.sku" placeholder="Opcional" />
            </label>
          <label>
            Código de barras
            <input v-model="form.barcode" placeholder="Ej. 7501234567890" />
          </label>
          </div>
          <label>
            Nombre
            <input v-model="form.name" required />
          </label>
          <label>
            Categoría
            <select v-model.number="form.category_id" @change="onCategoryChange">
              <option :value="null">(Sin categoría)</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </label>
          <label>
            Nueva categoría (opcional)
            <input 
              v-model="form.new_category" 
              placeholder="Crear nueva categoría si no existe" 
              @input="onNewCategoryInput"
            />
          </label>
          <label>
            Proveedor (nuevo)
            <input v-model="form.supplier_name" placeholder="Opcional" />
          </label>
          <label>
            Proveedor (existente)
            <select v-model.number="form.supplier_id">
              <option :value="null">(Ninguno)</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </label>
          <label>
            Costo
            <input v-model.number="form.cost_price" type="number" step="0.01" />
          </label>
      <label>
        Precio
        <input v-model.number="form.unit_price" type="number" step="0.01" />
      </label>
      <label>
        Descuento
        <select v-model.number="form.discount">
          <option :value="0">0%</option>
          <option v-for="d in [5,10,15,20,25,30,40,50]" :key="d" :value="d">{{ d }}%</option>
        </select>
      </label>
          <label>
            Impuesto
            <select v-model.number="form.tax_id">
              <option v-for="t in allowedTaxes" :key="t.id" :value="t.id">{{ t.name }} ({{ t.rate }}%)</option>
            </select>
          </label>
          <label>
            Punto de reorden
            <input v-model.number="form.reorder_level" type="number" step="1" />
          </label>
          <label>
            {{ isEdit ? 'Stock actual' : 'Inventario inicial (cantidad)' }}
            <input
              v-model.number="form.initial_qty"
              type="number"
              step="0.01"
            />
            <small v-if="isEdit" class="hint">Al guardar, se aplicará un ajuste de stock con la diferencia.</small>
          </label>
      <label class="span-2">
        Descripción
        <textarea v-model="form.description" rows="3" placeholder="Describe el producto"></textarea>
      </label>
      <label class="span-2">
        Notas
        <input v-model="form.notes" />
      </label>
        </div>
        <div v-if="loading" class="loading-overlay">Guardando…</div>
  </section>

      <footer class="modal-footer">
        <button class="btn" type="button" @click="save" :disabled="loading">{{ isEdit ? 'Guardar cambios' : 'Guardar' }}</button>
        <button class="btn secondary" type="button" @click="close">Cancelar</button>
      </footer>
    </div>
  </div>
  
</template>

<style scoped>
/* Modal Backdrop con animación */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 99999;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Modal Container con animación de entrada */
.modal {
  width: min(900px, 94vw);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #1c2027;
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(124, 92, 255, 0.25), 0 0 1px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(124, 92, 255, 0.1);
  display: grid;
  grid-template-rows: auto 1fr auto;
  max-height: 90vh;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* Dark mode para el modal */
@media (prefers-color-scheme: dark) {
  .modal {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border: 2px solid rgba(124, 92, 255, 0.3);
    color: var(--text);
  }
}

/* Header moderno con gradiente */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 2rem 1.5rem 2rem;
  border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #7c5cff, #2563eb, #7c5cff);
  border-image-slice: 1;
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, rgba(124, 92, 255, 0.08), rgba(37, 99, 235, 0.08));
  backdrop-filter: blur(10px);
  z-index: 10;
}

@media (prefers-color-scheme: dark) {
  .modal-header {
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.15), rgba(37, 99, 235, 0.15));
  }
}

.modal-header h2 {
  margin: 0;
  background: linear-gradient(135deg, #7c5cff, #2563eb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header h2::before {
  content: '📦';
  font-size: 2rem;
}

.close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s;
  line-height: 1;
}

.close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  transform: scale(1.1);
}

/* Body con scroll suave */
.modal-body {
  padding: 2rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: transparent;
}

/* Error mejorado */
.error {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error::before {
  content: '⚠️';
  font-size: 1.25rem;
}

/* Grid mejorado */
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.grid label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

@media (prefers-color-scheme: dark) {
  .grid label {
    color: #94a3b8;
  }
}

/* Inputs modernos */
.grid input,
.grid select,
.grid textarea {
  padding: 0.875rem 1.25rem;
  border: 2px solid rgba(124, 92, 255, 0.15);
  border-radius: 12px;
  background: white;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

@media (prefers-color-scheme: dark) {
  .grid input,
  .grid select,
  .grid textarea {
    background: rgba(255, 255, 255, 0.08);
    color: #f1f5f9;
    border-color: rgba(124, 92, 255, 0.25);
  }
}

.grid input:hover,
.grid select:hover,
.grid textarea:hover {
  border-color: rgba(124, 92, 255, 0.3);
  box-shadow: 0 4px 8px rgba(124, 92, 255, 0.1);
}

.grid input:focus,
.grid select:focus,
.grid textarea:focus {
  outline: none;
  border-color: #7c5cff;
  box-shadow: 0 0 0 4px rgba(124, 92, 255, 0.15), 0 4px 12px rgba(124, 92, 255, 0.2);
  transform: translateY(-1px);
}

.grid input::placeholder,
.grid textarea::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

/* Span columns */
.span-2 {
  grid-column: 1 / span 2;
}

.row-2.span-2 {
  grid-column: 1 / span 2;
}

.row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: start;
}

/* Image section mejorada */
.image-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.5rem;
}

.image-row input[type="file"] {
  padding: 0.75rem;
  border: 2px dashed rgba(124, 92, 255, 0.3);
  border-radius: 12px;
  background: rgba(124, 92, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.image-row input[type="file"]:hover {
  border-color: rgba(124, 92, 255, 0.5);
  background: rgba(124, 92, 255, 0.08);
}

.image-preview {
  margin-top: 1rem;
  border: 2px solid rgba(124, 92, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(124, 92, 255, 0.03);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.image-preview:hover {
  border-color: rgba(124, 92, 255, 0.4);
  box-shadow: 0 8px 24px rgba(124, 92, 255, 0.15);
}

.image-preview img {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-preview:hover img {
  transform: scale(1.05);
}

/* Small hint text */
small.hint {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 400;
  margin-top: 0.25rem;
  font-style: italic;
}

/* Footer moderno */
.modal-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, rgba(124, 92, 255, 0.05), rgba(37, 99, 235, 0.05));
  border-radius: 0 0 22px 22px;
  backdrop-filter: blur(10px);
  z-index: 10;
}

@media (prefers-color-scheme: dark) {
  .modal-footer {
    background: linear-gradient(135deg, rgba(124, 92, 255, 0.1), rgba(37, 99, 235, 0.1));
  }
}

/* Botones modernos */
.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 140px;
  justify-content: center;
}

.btn:not(.secondary) {
  background: linear-gradient(135deg, #7c5cff, #2563eb);
  color: white;
  box-shadow: 0 4px 16px rgba(124, 92, 255, 0.3);
  position: relative;
  overflow: hidden;
}

.btn:not(.secondary)::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:not(.secondary):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(124, 92, 255, 0.4);
}

.btn:not(.secondary):hover::before {
  left: 100%;
}

.btn:not(.secondary):active {
  transform: translateY(0);
}

.btn:not(.secondary):disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.btn.secondary {
  background: var(--surface);
  color: var(--text);
  border: 2px solid var(--border);
  box-shadow: none;
}

.btn.secondary:hover {
  background: rgba(124, 92, 255, 0.08);
  border-color: #7c5cff;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* Loading overlay mejorado */
.loading-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.3);
  font-weight: 700;
  color: white;
  font-size: 1.1rem;
  z-index: 100;
  animation: fadeIn 0.3s ease;
}

.loading-overlay::before {
  content: '⏳';
  font-size: 2rem;
  margin-right: 0.5rem;
}

/* Barcode preview (si se reactiva) */
.barcode-preview {
  margin-top: 0.75rem;
  border: 2px dashed rgba(124, 92, 255, 0.3);
  border-radius: 12px;
  background: rgba(124, 92, 255, 0.03);
  padding: 1rem;
  display: grid;
  place-items: center;
}

/* Responsivo */
@media (max-width: 720px) {
  .modal {
    width: 96vw;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 1.5rem 1rem;
  }
  
  .modal-header h2 {
    font-size: 1.5rem;
  }
  
  .modal-body {
    padding: 1.5rem 1rem;
  }
  
  .grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .span-2 {
    grid-column: auto;
  }
  
  .row-2 {
    grid-template-columns: 1fr;
  }
  
  .modal-footer {
    flex-wrap: wrap;
    padding: 1rem;
  }
  
  .btn {
    width: 100%;
    min-width: auto;
  }
}
</style>